import { isAuthenticated } from 'state/Auth'

/**
 * Syntactic sugar for time values, in milliseconds
 * @example window.setInterval(() => {}, 7 * MS.MIN + 25 * MS.SEC) //do nothing every 7min 25s
 */
export const MS = Object.freeze({
	SEC : 1_000,
	MIN : 60_000,
	HOUR: 3_600_000,
	DAY : 86_400_000,
})

/**
 * Remember that annoying Donkey asking "are we there yet" every minute or so? (https://youtu.be/Gn51xYvJhQI)
 * This class encapsulates that same feeling, taking that annoyance and the functions out of the global scope.
 *
 * Technically speaking, this is a timer class that will periodically {@link askAbout()} a specific {@link TOPIC}, according to a given `interval`, running a given `callback` every time the interval is hit, until you {@link giveUp()}.
 *
 * And while the Donkey class is a distant cousin of the {@link PumpkinTimer}, that other is way more complex, dealing with restaurant closure hours, timezones and day changes, being way more complex than a plain `setInterval()` abstraction.
 *
 * @abstract
 */
export class Donkey {

	static TOPIC = {
		ORDERS         : 'orders',
		THE_RESTAURANTS: 'restaurants',
	}

	static ORDERS_INTERVAL = MS.MIN

	/**
	 * Holds existing {@link setInterval} handlers, uniquely keyed by a {@link TOPIC}.
	 * @type {Map<TOPIC, NodeJS.Timeout>}
	 */
	static #timers = new Map() //looks cleaner than using [] and `delete` ¯\_(ツ)_/¯

	static #isValidTopic = topic => {
		if (Donkey.#isValidTopic.TOPICS.includes(topic)) {
			return topic
		}
		throw new RangeError(`🐴 The Donkey doesn't ask about "${topic}"! Valid: ${Donkey.#isValidTopic.TOPICS.join(', ')}.`)
	}
	static {
		this.#isValidTopic.TOPICS = Object.values(this.TOPIC)
	}

	/**
	 * @param topic {Donkey.TOPIC} One of the unique identifiers
	 * @returns {boolean}
	 */
	static alreadyAskingAbout = topic => Donkey.#isValidTopic(topic) && Donkey.#timers.has(topic)

	/**
	 * Creates a repeatedly-called, singleton and async function that only runs when the device is online and the user is authenticated - if they log out, the timer is automatically removed next time it tries to run.
	 * @param topic {Donkey.TOPIC} The unique identifier for what you'll be doing
	 * @param interval {MS|int} How frequently should we ask again the same thing?
	 * @param getState {function} The Redux `getState()`
	 * @param callback {function(state:object)} What you want to frequently `await` for (you're not obligated to define it as async, though). In every run, it receives as argument the updated state. However, if you need a dynamic state getter, I guess you can do it yourself, since you would have passed `getState` to the previous argument anyway 👀
	 */
	static askAbout(topic, interval, getState, callback) {
		if (Donkey.alreadyAskingAbout(topic)) {
			console.warn(`🐴 Tried to recreate the already active ${topic} timer`)
			return
		}

		console.debug(`🐴 Creating ${topic} timer, running every ${interval / MS.MIN} min`)
		const timer = setInterval(async () => {
			console.debug(`🐴 Are there updated ${topic} yet?`)
			if (!navigator.onLine) {
				console.debug(`🐴 Whoops, offline 🔌`)
				return
			}

			const state = getState()
			if (!isAuthenticated(state)) {
				console.debug(`🐴 Whoops, logged out 💥`)
				Donkey.giveUp(topic)
				return
			}

			//finally we can run this thing
			await callback(state)
		}, interval)

		Donkey.#timers.set(topic, timer)
	}

	/**
	 * For five minutes... Could you not be yourself for FIVE MINUTES?? FINALLY!
	 * @param what {Donkey.TOPIC} The unique identifier for what you don't need to ask about anymore
	 */
	static giveUp(what) {
		if (Donkey.alreadyAskingAbout(what)) {
			clearInterval(Donkey.#timers.get(what))
			Donkey.#timers.delete(what)
		}
	}
}
